import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { connect } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { compose } from 'recompose'

import { TaskOfflineAwardLeaderSignoff } from 'containers/forms/tasks'
import { AwardFiles, History } from 'containers/pages'
import { permissions as appPermissions } from 'helpers/decorators'
import { ResidentialProject } from 'containers/forms/activities'
import { AwardDetail, AwardState } from 'containers/forms/awards'
import { AdventureJourney } from 'containers/sections/activities'
import { AwardActivityTab } from 'components/sections/awards'
import { Section, SubTab, SubTabs } from 'components/layout'
import { Button } from 'components/misc'
import { AddActivityModal } from 'components/modals'
import { AwardDetailLoader } from 'components/loaders'
import {
  apiActivities,
  apiAwards,
  apiCodeLists,
  apiConfiguration,
  apiTasksRegistrationParticipants
} from 'helpers/decorators/api'
import { updateActiveTab } from 'redux/modules/app'
import {
  TasksAwardsRegistrationTableSingle,
  TasksAwardsTableSingle,
  TasksOfficeAwardsTableSingle,
  TasksUnassignedParticipantsTableSingle
} from 'components/sections/tasks'
import {
  ACTIVITY_SECTIONS,
  AWARD_TYPES,
  CONFIGURATION_KEYS,
  LEVELS,
  ORGANIZATION_MODES,
  PROGRESS_STATES
} from 'constants/enums'
import MODALS from 'constants/modals'

const btnStyles = { float: 'right', marginTop: 4 }

const enhance = compose(
  connect(
    ({ app }) => ({
      tabs: app.tabs
    }),
    { updateActiveTab }
  ),
  apiCodeLists(['activityReviseReasons']),
  appPermissions(),
  apiConfiguration(),
  apiAwards({
    list: true,
    detailConfiguration: ['ENABLE_ASS_MD', 'ENABLE_ASS_WM', 'ENABLE_MSG', 'ENABLE_BADGES', 'ENABLE_STRICT_BADGES', 'MANAG_ASSESSOR',
      'ENABLE_LOGS_BULK', 'ENABLE_LOGS_BULK_BRONZE', 'ENABLE_LOGS_BULK_SILVER', 'ENABLE_LOGS_BULK_GOLD', 'ENABLE_LOGS_BULK',
      'ENABLE_LOGS_BULK_BRONZE', 'ENABLE_LOGS_BULK_SILVER', 'ENABLE_LOGS_BULK_GOLD'],
    purgeOnUnmount: true,
    listReadParams: 'PAYMENT_INFO,LEADER,PARTICIPANT_REGISTRATION,ORGANIZATION_DETAIL,VALIDATIONS',
    updateList: true,
    updateParticipantDetail: true
  }),
  apiTasksRegistrationParticipants({
    updateParticipantAward: true
  }),
  apiActivities({
    activityCategories: true,
    updateParticipantDetails: true,
    updateParticipantAward: true
  })
)

class ParticipantLevel extends PureComponent {
  static propTypes = {
    route: PropTypes.object.isRequired,
    codeLists: PropTypes.object.isRequired,
    changeAwardState: PropTypes.func.isRequired,
    approveSignoffAward: PropTypes.func.isRequired,
    returnSignoffAward: PropTypes.func.isRequired,
    approveParticipantRegistration: PropTypes.func.isRequired,
    approveSignoffAwardOffice: PropTypes.func.isRequired,
    returnSignoffAwardOffice: PropTypes.func.isRequired,
    assignLeader: PropTypes.func.isRequired,
    updateActivity: PropTypes.func.isRequired,
    changeActivityAssessor: PropTypes.func.isRequired,
    setupActivity: PropTypes.func.isRequired,
    approveActivity: PropTypes.func.isRequired,
    returnActivity: PropTypes.func.isRequired,
    onGetAwardLeaderSignOffs: PropTypes.func.isRequired,
    signoffActivity: PropTypes.func.isRequired,
    returnSignoffActivity: PropTypes.func.isRequired,
    changeActivityState: PropTypes.func.isRequired,
    deleteParticipantRegistration: PropTypes.func.isRequired,
    onRegistrationAwardEdit: PropTypes.func.isRequired,
    modal: PropTypes.func.isRequired,
    updateActiveTab: PropTypes.func.isRequired,
    awards: PropTypes.object.isRequired,
    permissions: PropTypes.object.isRequired,
    activities: PropTypes.object.isRequired,
    onRestoreActivity: PropTypes.func.isRequired,
    refreshAwardsList: PropTypes.func.isRequired,
    configuration: PropTypes.object.isRequired,
    onGetAvailableAssessorsForActivityCategory: PropTypes.func,
    onGetConfigurationValue: PropTypes.func
  }

  constructor(props) {
    super(props)
    this.state = {
      defaultTab: 0
    }
  }

  componentDidMount() {
    const subTabId = get(this.props, 'params.subTabId')

    if (subTabId) {
      this.props.updateActiveTab('participantLevels', subTabId)
    }
  }

  getDefaultTab = () => {
    const { awards } = this.props
    const award = awards.list[0] || {}
    const isGold = award.residentialProject
      && get(award, 'awardLevel.value') === LEVELS.GOLD.value

    switch (get(this.props, 'params.subTabId')) {
      case ACTIVITY_SECTIONS.physical.link:
        return 1

      case ACTIVITY_SECTIONS.skills.link:
        return 2

      case ACTIVITY_SECTIONS.service.link:
        return 3

      case ACTIVITY_SECTIONS.aj.link:
        return 4

      case ACTIVITY_SECTIONS.rp.link:
        return 5

      case 'documents':
        return isGold ? 6 : 5

      default:
        return get(this.props, 'location.state.tabIndex') ||
          get(this.props, 'tabs.participantLevels') ||
          0
    }
  }

  handleTabChanged = id => {
    this.setState(
      {
        defaultTab: id
      },
      () => {
        this.props.updateActiveTab('participantLevels', id)
        this.props.updateActiveTab('activities', 0)
      }
    )
  }

  handleSubTabChanged = id => {
    this.props.updateActiveTab('activities', id)
  }

  showAddActivityModal = () => this.props.modal(MODALS.ADD_ACTIVITY)

  handleRefreshAward = () => {
    this.props.refreshAwardsList()
  }

  render() {
    const {
      awards,
      approveSignoffAward,
      returnSignoffAward,
      approveParticipantRegistration,
      approveSignoffAwardOffice,
      returnSignoffAwardOffice,
      onGetAwardLeaderSignOffs,
      assignLeader,
      activities,
      codeLists,
      route,
      permissions,
      configuration,
      onGetAvailableAssessorsForActivityCategory
    } = this.props
    const defaultTab = this.getDefaultTab()

    // first implementation didn't count with Award tabs ---
    // we don't have API to return certain award (eg Bronze) for participant,
    // so we download participant awards and filter locally
    const award = awards.list[0] || {}
    const isGold = get(award, 'awardLevel.value') === LEVELS.GOLD.value
    const isOffline = get(award, 'awardMode.value') === ORGANIZATION_MODES.offline.id
    const orgId = get(award, 'organization.id')
    const assessorDetailMandatoryCfg = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isAssessorDetailMandatory.id}]`)
    const isAssessorDetailMandatory = get(assessorDetailMandatoryCfg, 'booleanValue')
    const assessorWelcomeEmailCfg = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.enableAssessorWelcomeEmail.id}]`)
    const isAssessorWelcomeEmailEnabled = get(assessorWelcomeEmailCfg, 'booleanValue')
    const messagingCfg = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isMessagingEnabled.id}]`)
    const isMessagingEnabled = get(messagingCfg, 'booleanValue')
    const badgesCfg = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isBadgesEnabled.id}]`)
    const isBadgesEnabled = get(badgesCfg, 'booleanValue')
    const badgesSetting = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isBadgesStrictModeEnabled.id}]`)
    const isBadgesStrictModeEnabled = get(badgesSetting, 'booleanValue')
    const manageAssessorsSetting = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.manageAssessors.id}]`)
    const isAssessorManagementEnabled = get(manageAssessorsSetting, 'booleanValue')

    const activityData = {
      [ACTIVITY_SECTIONS.physical.id]: [],
      [ACTIVITY_SECTIONS.skills.id]: [],
      [ACTIVITY_SECTIONS.service.id]: []
    }

    if (award.activities) {
      award.activities.map(activity =>
        activityData[activity.activitySection.value].push(activity)
      )
    }

    const componentRegAssignment = (
      <TasksUnassignedParticipantsTableSingle
        data={award}
        approveModal={approveSignoffAwardOffice}
        assignLeader={assignLeader}
        modal={this.props.modal}
      />
    )
    const componentRegApproval = (
      <TasksAwardsRegistrationTableSingle
        data={award}
        approveModal={approveParticipantRegistration}
        modal={this.props.modal}
        deleteParticipantRegistration={this.props.deleteParticipantRegistration}
        onRegistrationAwardEdit={this.props.onRegistrationAwardEdit}
      />
    )
    const componentAwardSignoff = (
      <TasksAwardsTableSingle
        data={award}
        approveModal={approveSignoffAward}
        returnModal={returnSignoffAward}
        modal={this.props.modal}
      />
    )

    const componentAwardOfficeSignoff = awards.signOffAuthorized ? (
      <TasksOfficeAwardsTableSingle
        data={award}
        approveModal={approveSignoffAwardOffice}
        returnModal={returnSignoffAwardOffice}
        onGetAwardLeaderSignOffs={onGetAwardLeaderSignOffs}
        modal={this.props.modal}
      />
    ) : null

    const componentOfflineAwardSignoff = (
      <TaskOfflineAwardLeaderSignoff
        awardId={get(award, 'id')}
        isGold={isGold}
        onAwardChange={this.props.changeAwardState}
      />
    )

    if (!awards.loading && !award.id) {
      return <FormattedMessage id="label.no_data" defaultMessage="No data" />
    }

    const commonProps = {
      awardProps: {
        activityData,
        award,
        onTabChange: this.handleTabChanged,
        onSubTabChanged: this.handleSubTabChanged
      },
      activityProps: {
        route,
        activities,
        awards,
        approveActivity: this.props.approveActivity,
        returnActivity: this.props.returnActivity,
        signoffActivity: this.props.signoffActivity,
        returnSignoffActivity: this.props.returnSignoffActivity,
        updateActivity: this.props.updateActivity,
        changeActivityAssessor: this.props.changeActivityAssessor,
        setupActivity: this.props.setupActivity,
        changeActivityState: this.props.changeActivityState,
        onRestoreActivity: this.props.onRestoreActivity
      }
    }

    const isSimplified = get(award, 'awardType.value') === AWARD_TYPES.simplified.id
    const isBronze = get(award, 'awardLevel.value') === LEVELS.BRONZE.value
    const isSimplifiedBronze = isSimplified && isBronze

    return (
      <Section loading={awards.loading} contentLoader={<AwardDetailLoader />}>
        <div style={{ marginTop: 20 }}>
          {!isOffline &&
            <Button
              type="button"
              style={btnStyles}
              secondary
              tiny
              onClick={this.showAddActivityModal}
              disabled={
                get(award, 'awardState.value') ===
                PROGRESS_STATES.awardInactive.id
              }
            >
              <FormattedMessage id="button.add_activity" defaultMessage="Add Activity" />
            </Button>}
          <SubTabs
            onChange={this.handleTabChanged}
            defaultActive={defaultTab}
            activeTab={2}
            id="participantLevels"
            equalWidth
          >
            <SubTab
              title="Award detail"
              intlKey="award.award_detail"
              onChange={this.handleTabChanged}
            >
              <div className="row">
                <div className="col-xs-12">
                  {award.id &&
                    <AwardState
                      awardId={award.id}
                      award={award}
                      awardState={get(award, 'awardState')}
                      awardMode={get(award, 'awardMode')}
                      changeAwardState={this.props.changeAwardState}
                      componentRegApproval={componentRegApproval}
                      componentAwardSignoff={componentAwardSignoff}
                      componentRegAssignment={componentRegAssignment}
                      componentAwardOfficeSignoff={componentAwardOfficeSignoff}
                      componentOfflineAwardSignoff={
                        componentOfflineAwardSignoff
                      }
                      isSuperior={permissions.isSuperior}
                      isVerifier={permissions.isVerifier}
                    />}
                </div>

                {get(award, 'id') &&
                  <div className="col-xs-12">
                    <AwardDetail
                      award={award}
                      refreshAward={this.handleRefreshAward}
                      form="awardDetail"
                      modal={this.props.modal}
                      permissions={permissions}
                      {...this.props}
                    />
                  </div>}
              </div>

              {get(award, 'id') &&
                <History
                  decoActions="award"
                  decoAwardId={award.id}
                  key={award.id}
                  showAccordion
                />}
            </SubTab>

            <AwardActivityTab
              {...commonProps}
              actId="physical"
              svg="physical"
              title="Physical recreation"
              intlKey="award.physical_recreation"
              isAssessorDetailMandatory={isAssessorDetailMandatory}
              isAssessorWelcomeEmailEnabled={isAssessorWelcomeEmailEnabled}
              isMessagingEnabled={isMessagingEnabled}
              isBadgesEnabled={isBadgesEnabled}
              isBadgesStrictModeEnabled={isBadgesStrictModeEnabled}
            />

            <AwardActivityTab
              {...commonProps}
              actId="skills"
              svg="skills"
              title="Skills"
              intlKey="award.skills"
              isAssessorDetailMandatory={isAssessorDetailMandatory}
              isAssessorWelcomeEmailEnabled={isAssessorWelcomeEmailEnabled}
              isMessagingEnabled={isMessagingEnabled}
              isBadgesEnabled={isBadgesEnabled}
              isBadgesStrictModeEnabled={isBadgesStrictModeEnabled}
            />

            <AwardActivityTab
              {...commonProps}
              actId="service"
              svg="service"
              title="Service"
              intlKey="award.service"
              isAssessorDetailMandatory={isAssessorDetailMandatory}
              isAssessorWelcomeEmailEnabled={isAssessorWelcomeEmailEnabled}
              isMessagingEnabled={isMessagingEnabled}
              isBadgesEnabled={isBadgesEnabled}
              isBadgesStrictModeEnabled={isBadgesStrictModeEnabled}
            />

            <SubTab
              title="Adventurous journey"
              intlKey="award.aj"
              svg="aj"
              progress={`${award.ajProgress}`}
            >
              <AdventureJourney
                decoFilter="awardEvents"
                awardId={get(award, 'id')}
                isParentLoading={awards.loading}
                isOffline={isOffline}
                codeLists={codeLists}
                isSimplifiedBronze={isSimplifiedBronze}
              />
            </SubTab>
            {award.residentialProject &&
              isGold &&
              <SubTab intlKey="award.rp" title="Residential project" svg="rp">
                {get(award, 'residentialProject.id') &&
                  <ResidentialProject
                    form={`residentialProject-${award.id}`}
                    currentLevelId={get(award, 'awardLevel.value')}
                    decoRpId={award.residentialProject.id}
                    activities={activities}
                    awardState={award.awardState}
                    award={award}
                    projectLocationTypes={codeLists.projectLocationTypes}
                    modal={this.props.modal}
                    route={route}
                    isOffline={isOffline}
                    isAssessorDetailMandatory={isAssessorDetailMandatory}
                    titles={codeLists.titles.list}
                    codeLists={codeLists}
                    orgId={get(award, 'organization.id')}
                  />}
              </SubTab>}
            <SubTab title="Documents" intlKey="tabs.documents">
              {get(award, 'id') &&
                <AwardFiles
                  decoAwardId={award.id}
                  isParentLoading={awards.loading}
                />}
            </SubTab>
          </SubTabs>

          <AddActivityModal
            awardId={get(award, 'id')}
            activities={activities}
            groupedActivities={activityData}
            modalId={MODALS.ADD_ACTIVITY}
            modal={this.props.modal}
            route={route}
            isAssessorDetailMandatory={isAssessorDetailMandatory}
            isAssessorManagementEnabled={isAssessorManagementEnabled}
            onGetAvailableAssessorsForActivityCategory={onGetAvailableAssessorsForActivityCategory}
            orgId={orgId}
          />
        </div>
      </Section>
    )
  }
}

export default enhance(ParticipantLevel)
